/**
 * Marketing Flex Table — the pivot engine.
 *
 * Turns the API's grouped facts plus the user's table setup into a grid. Pure:
 * no React, no I/O, no mutation of its inputs.
 *
 * Every record family is first normalised into one shape. A family that does
 * not carry a dimension (appointments have no UTM, leads no appointment type,
 * …) gets id 0 for it, which resolves to "Not set" exactly like an unset or
 * unknown id does (R12). That is what keeps Totals identical whatever the
 * grouping: every record lands in exactly one bucket.
 */

import { toYmd } from '@/lib/utils';
import {
  PATIENT_TYPE_EXISTING,
  PATIENT_TYPE_NEW,
  type FlexFacts,
} from '@/types/marketingFlexTable.types';
import {
  DIMENSIONS,
  METRICS,
  NOT_SET_LABEL,
  addMeasures,
  emptyMeasures,
  type DimensionKey,
  type Measures,
  type MetricFormat,
  type MetricKey,
} from './flexCatalog';

export type FlexView = 'nested' | 'side-by-side';

export interface FlexSort {
  by: 'dimension' | MetricKey;
  dir: 'asc' | 'desc';
}

export interface FlexSetup {
  primary: DimensionKey | null;
  secondary: DimensionKey | null;
  view: FlexView;
  metrics: MetricKey[];
  sort: FlexSort | null;
}

export interface FlexGridOptions {
  /** Keys of nested parents the user has opened. Parents are collapsed otherwise (R24). */
  expanded: Set<string>;
  search: string;
  /** The Patient Type filter selects exactly one type, so every row holds that one type. */
  onePatientType?: boolean;
}

export interface FlexColumn {
  key: string;
  label: string;
  kind: 'dimension' | 'metric';
  format: 'text' | MetricFormat;
  metric?: MetricKey;
}

export interface FlexGridRow {
  key: string;
  level: 0 | 1;
  parentKey: string | null;
  /** One cell per dimension column: two in side-by-side, otherwise one. */
  labels: string[];
  /** One value per selected metric, in `FlexGrid.metrics` order. null = empty. */
  values: (number | null)[];
  measures: Measures;
  hasChildren: boolean;
  /** Children of this row are shown in `rows`. Always false for leaf rows. */
  expanded: boolean;
  isTotal: boolean;
  notSet: boolean;
}

export interface FlexGrid {
  columns: FlexColumn[];
  dimensionColumnCount: 1 | 2;
  metrics: MetricKey[];
  /** Always first on screen and in export; never affected by search (R19). */
  totals: FlexGridRow;
  /** What the table shows below Totals: searched, sorted, collapsed parents' children hidden. */
  rows: FlexGridRow[];
  /** Every row the export carries below Totals: searched and sorted, regardless of expand state (R26). */
  exportRows: FlexGridRow[];
}

export const TOTALS_KEY = '__totals__';
const NOT_SET_KEY = '0';

/* ------------------------------------------------------------------ records */

interface NormalRecord {
  day: string;
  sourceId: number;
  facilityId: number;
  patientType: number;
  utm: [number, number, number, number, number];
  planTypeId: number;
  appointmentTypeId: number;
  measures: Measures;
}

function measures(partial: Partial<Measures>): Measures {
  return { ...emptyMeasures(), ...partial };
}

const PATIENT_TYPE_LABELS: Record<number, string> = {
  [PATIENT_TYPE_NEW]: 'New',
  [PATIENT_TYPE_EXISTING]: 'Existing',
};

/**
 * One response's records and totals, decoded once. Every control except the
 * period and filters re-runs buildGrid over the same facts object, so this is
 * cached per response rather than redone per keystroke. Callers never mutate
 * what comes back — buildGrid copies the totals before using them.
 */
const decodedCache = new WeakMap<FlexFacts, { records: NormalRecord[]; totals: Measures }>();

function decoded(facts: FlexFacts): { records: NormalRecord[]; totals: Measures } {
  let hit = decodedCache.get(facts);
  if (!hit) {
    const records = normalise(facts);
    const totals = emptyMeasures();
    for (const r of records) addMeasures(totals, r.measures);
    hit = { records, totals };
    decodedCache.set(facts, hit);
  }
  return hit;
}

function normalise(facts: FlexFacts): NormalRecord[] {
  const out: NormalRecord[] = [];
  for (const [day, sourceId, facilityId, patientType, s, m, c, t, ct, planTypeId, leads, converted] of facts.leads) {
    out.push({
      day,
      sourceId,
      facilityId,
      patientType,
      utm: [s, m, c, t, ct],
      planTypeId,
      appointmentTypeId: 0,
      measures: measures({ leads, converted }),
    });
  }
  for (const [day, sourceId, facilityId, patientType, planTypeId, appointmentTypeId, booked, showed, noShows, cancelled] of facts.appointments) {
    out.push({
      day,
      sourceId,
      facilityId,
      patientType,
      utm: [0, 0, 0, 0, 0],
      planTypeId,
      appointmentTypeId,
      measures: measures({ booked, showed, noShows, cancelled }),
    });
  }
  for (const [day, sourceId, facilityId, planTypeId, newPatients] of facts.newPatients) {
    out.push({
      day,
      sourceId,
      facilityId,
      patientType: PATIENT_TYPE_NEW,
      utm: [0, 0, 0, 0, 0],
      planTypeId,
      appointmentTypeId: 0,
      measures: measures({ newPatients }),
    });
  }
  for (const [day, sourceId, facilityId, patientType, planTypeId, revenueCents] of facts.payments) {
    out.push({
      day,
      sourceId,
      facilityId,
      patientType,
      utm: [0, 0, 0, 0, 0],
      planTypeId,
      appointmentTypeId: 0,
      measures: measures({ revenueCents }),
    });
  }
  // The page and the API deploy separately; an API without this family yet
  // reads as no new customers rather than a blank page.
  for (const [day, sourceId, facilityId, planTypeId, newCustomers] of facts.newCustomers ?? []) {
    out.push({
      day,
      sourceId,
      facilityId,
      patientType: PATIENT_TYPE_NEW,
      utm: [0, 0, 0, 0, 0],
      planTypeId,
      appointmentTypeId: 0,
      measures: measures({ newCustomers }),
    });
  }
  return out;
}

/* --------------------------------------------------------------- dimensions */

interface DimValue {
  key: string;
  label: string;
  /** Natural-order key: ISO date prefix for date dimensions, lower-cased label otherwise. */
  sort: string;
  notSet: boolean;
}

const NOT_SET: DimValue = { key: NOT_SET_KEY, label: NOT_SET_LABEL, sort: '', notSet: true };

/** Local midnight — `new Date('YYYY-MM-DD')` is UTC midnight and lands on the previous day in the Americas. */
function parseDay(day: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DAY_LABEL = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

function named(id: number, name: string | undefined): DimValue {
  if (id === 0 || name === undefined || name === '') return NOT_SET;
  return { key: String(id), label: name, sort: name.toLowerCase(), notSet: false };
}

type Resolver = (r: NormalRecord) => DimValue;

function resolverFor(dimension: DimensionKey, facts: FlexFacts): Resolver {
  const dict = facts.dictionaries;
  const dateCache = new Map<string, DimValue>();
  const dated = (bucket: (d: Date) => DimValue): Resolver => (r) => {
    const cached = dateCache.get(r.day);
    if (cached) return cached;
    const d = parseDay(r.day);
    const v = d ? bucket(d) : NOT_SET;
    dateCache.set(r.day, v);
    return v;
  };
  const utm = (index: number): Resolver => (r) => {
    const id = r.utm[index] ?? 0;
    return named(id, dict.utm[id]);
  };

  switch (dimension) {
    case 'day':
      return dated((d) => {
        const key = toYmd(d);
        return { key, label: DAY_LABEL.format(d), sort: key, notSet: false };
      });
    case 'week':
      return dated((d) => {
        const sunday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
        const key = toYmd(sunday);
        return { key, label: `Week of ${DAY_LABEL.format(sunday)}`, sort: key, notSet: false };
      });
    case 'month':
      return dated((d) => {
        const key = toYmd(d).slice(0, 7);
        return { key, label: MONTH_LABEL.format(d), sort: key, notSet: false };
      });
    case 'year':
      return dated((d) => {
        const key = String(d.getFullYear());
        return { key, label: key, sort: key, notSet: false };
      });
    // A negative sourceId is a category-only record (the API had a Lead Source
    // Category but no specific source): "Not set" here, its category below.
    case 'source':
      return (r) => (r.sourceId <= 0 ? NOT_SET : named(r.sourceId, dict.sources[String(r.sourceId)]?.name));
    case 'sourceCategory':
      return (r) => {
        const categoryId = r.sourceId === 0 ? 0 : dict.sources[String(r.sourceId)]?.categoryId ?? 0;
        return named(categoryId, dict.categories[String(categoryId)]);
      };
    case 'patientType':
      return (r) => named(r.patientType, PATIENT_TYPE_LABELS[r.patientType]);
    case 'utmSource':
      return utm(0);
    case 'utmMedium':
      return utm(1);
    case 'utmCampaign':
      return utm(2);
    case 'utmTerm':
      return utm(3);
    case 'utmContent':
      return utm(4);
    case 'location':
      return (r) => named(r.facilityId, dict.facilities[String(r.facilityId)]);
    case 'planType':
      return (r) => named(r.planTypeId, dict.planTypes[String(r.planTypeId)]);
    case 'appointmentType':
      return (r) => named(r.appointmentTypeId, dict.appointmentTypes[String(r.appointmentTypeId)]);
  }
}

/* ------------------------------------------------------------------ buckets */

interface Bucket {
  value: DimValue;
  measures: Measures;
  children: Map<string, Bucket>;
}

function bucketIn(map: Map<string, Bucket>, value: DimValue): Bucket {
  let b = map.get(value.key);
  if (!b) {
    b = { value, measures: emptyMeasures(), children: new Map() };
    map.set(value.key, b);
  }
  return b;
}

/* -------------------------------------------------------------------- sort */

interface SortItem {
  /** Dimension values from outermost to innermost, compared in order. */
  values: DimValue[];
  measures: Measures;
}

function naturalCompare(a: DimValue, b: DimValue, dir: 1 | -1): number {
  // "Not set" stays last in natural and dimension order, whichever direction.
  if (a.notSet !== b.notSet) return a.notSet ? 1 : -1;
  if (a.sort !== b.sort) return a.sort < b.sort ? -dir : dir;
  if (a.key !== b.key) return a.key < b.key ? -dir : dir;
  return 0;
}

function compareDims(a: SortItem, b: SortItem, dir: 1 | -1): number {
  for (let i = 0; i < Math.max(a.values.length, b.values.length); i += 1) {
    const av = a.values[i];
    const bv = b.values[i];
    if (!av || !bv) continue;
    const c = naturalCompare(av, bv, dir);
    if (c !== 0) return c;
  }
  return 0;
}

function comparator(sort: FlexSort | null, blanked: ReadonlySet<MetricKey>): (a: SortItem, b: SortItem) => number {
  if (!sort) return (a, b) => compareDims(a, b, 1);
  const dir: 1 | -1 = sort.dir === 'asc' ? 1 : -1;
  if (sort.by === 'dimension') return (a, b) => compareDims(a, b, dir);
  const compute = blanked.has(sort.by) ? () => null : METRICS[sort.by].compute;
  return (a, b) => {
    const av = compute(a.measures);
    const bv = compute(b.measures);
    // Empty values (zero-denominator ratios) sink to the bottom in both directions.
    if (av === null && bv !== null) return 1;
    if (bv === null && av !== null) return -1;
    if (av !== null && bv !== null && av !== bv) return (av - bv) * dir;
    return compareDims(a, b, 1);
  };
}

function sorted<T extends SortItem>(items: T[], sort: FlexSort | null, blanked: ReadonlySet<MetricKey>): T[] {
  return [...items].sort(comparator(sort, blanked));
}

/* -------------------------------------------------------------------- grid */

function metricValues(m: Measures, metrics: MetricKey[], blanked: ReadonlySet<MetricKey>): (number | null)[] {
  return metrics.map((key) => (blanked.has(key) ? null : METRICS[key].compute(m)));
}

function matches(label: string, needle: string): boolean {
  return needle === '' || label.toLowerCase().includes(needle);
}

export function buildGrid(facts: FlexFacts, setup: FlexSetup, options: FlexGridOptions): FlexGrid {
  const metrics = [...setup.metrics];
  const primary = setup.primary;
  const secondary = primary !== null && setup.secondary !== primary ? setup.secondary : null;
  const sideBySide = secondary !== null && setup.view === 'side-by-side';
  const dimensionColumnCount: 1 | 2 = sideBySide ? 2 : 1;
  const needle = options.search.trim().toLowerCase();
  // Some metrics show blank on a row that holds a single patient type (see
  // blankByPatientType). Totals and rows above the Patient Type level hold
  // both types, unless the filter selects exactly one.
  const blankable = metrics.filter((key) => METRICS[key].blankByPatientType === true);
  const blankWhen = (oneType: boolean) => new Set<MetricKey>(oneType ? blankable : []);
  const onePatientType = options.onePatientType === true;
  const totalsBlanked = blankWhen(onePatientType);
  const topBlanked = blankWhen(onePatientType || primary === 'patientType');
  const innerBlanked = blankWhen(onePatientType || primary === 'patientType' || secondary === 'patientType');

  const { records, totals: decodedTotals } = decoded(facts);
  const totalMeasures = emptyMeasures();
  addMeasures(totalMeasures, decodedTotals);

  const columns: FlexColumn[] = [];
  if (sideBySide && primary && secondary) {
    columns.push({ key: 'dim:primary', label: DIMENSIONS[primary].label, kind: 'dimension', format: 'text' });
    columns.push({ key: 'dim:secondary', label: DIMENSIONS[secondary].label, kind: 'dimension', format: 'text' });
  } else {
    const label = primary
      ? secondary
        ? `${DIMENSIONS[primary].label} / ${DIMENSIONS[secondary].label}`
        : DIMENSIONS[primary].label
      : '';
    columns.push({ key: 'dim:primary', label, kind: 'dimension', format: 'text' });
  }
  for (const key of metrics) {
    columns.push({ key: `metric:${key}`, label: METRICS[key].label, kind: 'metric', format: METRICS[key].format, metric: key });
  }

  const totals: FlexGridRow = {
    key: TOTALS_KEY,
    level: 0,
    parentKey: null,
    labels: sideBySide ? ['Totals', ''] : ['Totals'],
    values: metricValues(totalMeasures, metrics, totalsBlanked),
    measures: totalMeasures,
    hasChildren: false,
    expanded: false,
    isTotal: true,
    notSet: false,
  };

  const grid = (rows: FlexGridRow[], exportRows: FlexGridRow[]): FlexGrid => ({
    columns,
    dimensionColumnCount,
    metrics,
    totals,
    rows,
    exportRows,
  });

  // R13: no primary dimension, nothing below Totals.
  if (!primary) return grid([], []);

  const resolvePrimary = resolverFor(primary, facts);
  const resolveSecondary = secondary ? resolverFor(secondary, facts) : null;

  const top = new Map<string, Bucket>();
  for (const r of records) {
    const parent = bucketIn(top, resolvePrimary(r));
    addMeasures(parent.measures, r.measures);
    if (resolveSecondary) addMeasures(bucketIn(parent.children, resolveSecondary(r)).measures, r.measures);
  }

  const leaf = (
    key: string,
    level: 0 | 1,
    parentKey: string | null,
    labels: string[],
    m: Measures,
    notSet: boolean,
    blanked: ReadonlySet<MetricKey>,
  ): FlexGridRow => ({
    key,
    level,
    parentKey,
    labels,
    values: metricValues(m, metrics, blanked),
    measures: m,
    hasChildren: false,
    expanded: false,
    isTotal: false,
    notSet,
  });

  // Single dimension: flat rows.
  if (!resolveSecondary) {
    const items = sorted(
      [...top.values()].map((b) => ({ values: [b.value], measures: b.measures, bucket: b })),
      setup.sort,
      topBlanked,
    );
    const rows = items
      .filter((i) => matches(i.bucket.value.label, needle))
      .map((i) => leaf(`p:${i.bucket.value.key}`, 0, null, [i.bucket.value.label], i.measures, i.bucket.value.notSet, topBlanked));
    return grid(rows, rows);
  }

  // Side-by-side: one flat row per primary+secondary pair, sorted as a whole.
  if (sideBySide) {
    const pairs: (SortItem & { parent: DimValue; child: DimValue })[] = [];
    for (const parent of top.values()) {
      for (const child of parent.children.values()) {
        pairs.push({ values: [parent.value, child.value], measures: child.measures, parent: parent.value, child: child.value });
      }
    }
    const rows = sorted(pairs, setup.sort, innerBlanked)
      .filter((p) => matches(p.parent.label, needle) || matches(p.child.label, needle))
      .map((p) =>
        leaf(
          `p:${p.parent.key}|s:${p.child.key}`,
          0,
          null,
          [p.parent.label, p.child.label],
          p.measures,
          p.parent.notSet || p.child.notSet,
          innerBlanked,
        ),
      );
    return grid(rows, rows);
  }

  // Nested: parents sort among themselves by subtotal, children within their parent.
  const rows: FlexGridRow[] = [];
  const exportRows: FlexGridRow[] = [];
  const parents = sorted(
    [...top.values()].map((b) => ({ values: [b.value], measures: b.measures, bucket: b })),
    setup.sort,
    topBlanked,
  );
  for (const { bucket } of parents) {
    const parentKey = `p:${bucket.value.key}`;
    const parentMatches = matches(bucket.value.label, needle);
    const allChildren = sorted(
      [...bucket.children.values()].map((c) => ({ values: [c.value], measures: c.measures, bucket: c })),
      setup.sort,
      innerBlanked,
    );
    const matchingChildren = allChildren.filter((c) => matches(c.bucket.value.label, needle));
    if (!parentMatches && matchingChildren.length === 0) continue;

    const shownChildren = parentMatches ? allChildren : matchingChildren;
    // R23: while searching, a parent with a matching child opens so the match is visible.
    const forcedOpen = needle !== '' && matchingChildren.length > 0;
    const isOpen = shownChildren.length > 0 && (forcedOpen || options.expanded.has(parentKey));

    const parentRow: FlexGridRow = {
      ...leaf(parentKey, 0, null, [bucket.value.label], bucket.measures, bucket.value.notSet, topBlanked),
      hasChildren: shownChildren.length > 0,
      expanded: isOpen,
    };
    const childRows = shownChildren.map((c) =>
      leaf(`${parentKey}|s:${c.bucket.value.key}`, 1, parentKey, [c.bucket.value.label], c.measures, c.bucket.value.notSet, innerBlanked),
    );

    exportRows.push(parentRow, ...childRows);
    rows.push(parentRow);
    if (isOpen) rows.push(...childRows);
  }
  return grid(rows, exportRows);
}
